import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Ticket,
  LogOut,
  Store,
  Truck,
  ShieldCheck,
  ChevronRight
} from "lucide-react";
import Logo from "../Logo";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";

const NAV = [
  { to: "/admin", label: "Tableau de Bord & Stats", icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "Gestion des Commandes (CRM)", icon: ShoppingCart },
  { to: "/admin/products", label: "Catalogue Sérums", icon: Package },
  { to: "/admin/users", label: "Comptes Clients", icon: Users },
  { to: "/admin/coupons", label: "Codes Promotionnels", icon: Ticket },
  { to: "/admin/livraison", label: "Tarifs 58 Wilayas", icon: Truck },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#F8F8F6] text-[#111111]">
      
      {/* Desktop Luxury Sidebar */}
      <aside className="hidden md:flex flex-col w-[270px] bg-[#111111] text-white fixed inset-y-0 left-0 z-30 shadow-2xl">
        
        {/* Brand Header */}
        <div className="px-6 py-7 border-b border-white/10">
          <div className="inline-flex bg-white rounded-full px-3 py-1.5 shadow-sm">
            <Logo height={26} />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#C5A880] flex items-center gap-1.5">
              <ShieldCheck size={12} className="text-[#D97706]" />
              CRM Administrateur
            </span>
            <span className="text-[9px] bg-white/10 text-emerald-400 px-2 py-0.5 rounded-full font-mono">
              En direct
            </span>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto no-scrollbar">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center justify-between px-3.5 py-3 rounded-xl font-body text-xs tracking-wide transition-all ${
                  isActive
                    ? "bg-white text-[#111111] font-semibold shadow-md"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="flex items-center gap-3">
                    <Icon size={16} className={isActive ? "text-[#D97706]" : "text-white/60"} />
                    <span>{label}</span>
                  </div>
                  {isActive && <ChevronRight size={14} className="text-[#111111]" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Language selector in sidebar */}
        <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between text-[11px] text-white/60">
          <span>Langue CRM :</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setLang("fr")}
              className={`px-2 py-0.5 rounded text-[10px] ${lang === "fr" ? "bg-white text-black font-bold" : "text-white/70 hover:text-white"}`}
            >
              FR
            </button>
            <button
              onClick={() => setLang("ar")}
              className={`px-2 py-0.5 rounded text-[10px] ${lang === "ar" ? "bg-white text-black font-bold" : "text-white/70 hover:text-white"}`}
            >
              عربي
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-2 py-0.5 rounded text-[10px] ${lang === "en" ? "bg-white text-black font-bold" : "text-white/70 hover:text-white"}`}
            >
              EN
            </button>
          </div>
        </div>

        {/* User Info & Actions */}
        <div className="p-4 border-t border-white/10 space-y-1 bg-black/40">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 px-3 py-2 rounded-xl w-full text-white/70 hover:bg-white/10 hover:text-white text-xs font-body transition-colors"
          >
            <Store size={15} /> Voir la boutique
          </button>
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="flex items-center gap-2.5 px-3 py-2 rounded-xl w-full text-red-400 hover:bg-red-500/10 text-xs font-body transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={15} /> Déconnexion
          </button>
          <div className="pt-2 px-1">
            <p className="text-[11px] text-white/90 font-medium truncate">{user?.full_name || "The Queen Admin"}</p>
            <p className="text-[10px] text-white/40 truncate">{user?.email}</p>
          </div>
        </div>
      </aside>

      {/* Mobile Top Navbar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#111111] text-white border-b border-white/10">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="bg-white rounded-full px-2.5 py-1">
            <Logo height={22} />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/")}
              className="text-xs bg-white/10 px-2.5 py-1 rounded-full text-white"
            >
              Boutique
            </button>
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="text-xs bg-red-900/60 px-2.5 py-1 rounded-full text-red-200"
            >
              Quitter
            </button>
          </div>
        </div>
        <div className="flex gap-1 overflow-x-auto px-2 py-2 no-scrollbar bg-black/50 border-t border-white/5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-lg text-xs whitespace-nowrap flex items-center gap-1.5 ${
                  isActive ? "bg-white text-black font-semibold" : "bg-white/10 text-white/70"
                }`
              }
            >
              <Icon size={13} />
              <span>{label.split(" ")[0]}</span>
            </NavLink>
          ))}
        </div>
      </div>

      {/* Main Content Viewport */}
      <main className="flex-1 md:ml-[270px] pt-24 md:pt-0">
        <div className="p-6 md:p-10 max-w-[1280px] mx-auto">
          <Outlet />
        </div>
      </main>

    </div>
  );
}
